import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';
import { useStorage } from '~Components/Storage';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useDeviceInfo, useLogger, useSelector, useStaffCan } from '~Hooks';
import AdyenTerminalButton from '~Scenes/Tables/Components/PayButtons/AdyenTerminalButton';
import { BlackButton, Box, Button, Dimmer, GreenButton } from '~Styles';
import { handyBreakpoint } from '~Styles/breakpoints';
import useI18n from '~Translations';

const PayWrapper = styled.div`
  position: relative;
  grid-column: span 2;
  width: 100%;
`;

const TerminalSelectWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 8px;
  border-radius: 4px;
  background-color: white;
  padding: 16px 8px 8px;
  z-index: 3;
  width: 100%;
  min-width: 240px;
  box-sizing: border-box;
`;

const TerminalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  ${handyBreakpoint} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

interface Props {
  onPay: (paymentMethodId: string, poiid?: string) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  isQsr?: boolean;
  /** When true, automatically triggers payment on mount using the default payment method */
  autoInitiatePayment?: boolean;
  /** Called when auto-payment has been initiated, so the parent can reset the flag */
  onAutoPaymentInitiated?: () => void;
}

const PayButton: React.FC<Props> = ({
  onPay,
  style,
  disabled,
  isQsr,
  autoInitiatePayment,
  onAutoPaymentInitiated,
}) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const deviceInfo = useDeviceInfo();
  const logger = useLogger('pay-button');
  const staffCan = useStaffCan();

  const deviceWakeState = useSelector((state) => state.deviceWakeState);
  const venueId = useSelector((state) => state.venueId);
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const filteredPaymentMethods = paymentMethods.filter(
    (pm) => pm.venues.some((v) => v === venueId) || pm.venues.length === 0,
  );
  const busyTerminals = useSelector((state) => state.POSPayments)
    .filter((p) => p.variant && p.status === 'PENDING')
    .map((p) => p.variant);

  const [showTerminalSelection, setShowTerminalSelection] = useState<boolean>(false);

  const primaryPaymentMethod = filteredPaymentMethods.find((pm) => pm.isPrimary);
  const hasAdyen = filteredPaymentMethods.some((method) => method.id === 'ADYEN');
  const hasZettle = filteredPaymentMethods.some((method) => method.id === 'IZETTLE');

  // If the device has a specific payment method assigned, use that
  // Otherwise, use the primary payment method if available
  // Otherwise, fall back to ADYEN or IZETTLE if available
  const defaultPaymentMethod =
    deviceInfo?.specificPaymentMethod ||
    primaryPaymentMethod?.id ||
    (hasAdyen && 'ADYEN') ||
    (hasZettle && 'IZETTLE');

  const defaultPayButton = useButton({
    onClick: () => {
      !isQsr && storage.adyenTerminals.length > 1
        ? setShowTerminalSelection(true)
        : onPay(defaultPaymentMethod || 'ADYEN', storage.adyenTerminals[0]?.POIID);
    },
    disabled:
      disabled ||
      !staffCan.MAKE_PAYMENT ||
      (deviceInfo?.isHandheldTerminal && deviceWakeState.syncingTableEvents),
    disabledToast:
      deviceInfo?.isHandheldTerminal && deviceWakeState.syncingTableEvents
        ? i18n('Handy.Tables.LoadingEvents')
        : i18n('Staff.NoPermission.MakePayment'),
  });

  // Auto-trigger the same action as pressing the pay button
  const hasAutoInitiated = useRef(false);
  useEffect(() => {
    if (autoInitiatePayment && defaultPaymentMethod && !hasAutoInitiated.current) {
      hasAutoInitiated.current = true;
      logger.info('Auto-initiating payment (direct payment mode)');
      onPay(defaultPaymentMethod || 'ADYEN', storage.adyenTerminals[0]?.POIID);
      onAutoPaymentInitiated?.();
    }
  }, [autoInitiatePayment, defaultPaymentMethod]);

  const adyenSelectCancelButton = useButton({
    onClick: () => {
      setShowTerminalSelection(false);
    },
  });

  // If there is no default payment method, do not render the button since the pay with button will be used instead
  if (!defaultPaymentMethod) {
    return null;
  }

  return (
    <PayWrapper>
      {showTerminalSelection && (
        <Dimmer
          as="span"
          active
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowTerminalSelection(false);
          }}
        />
      )}

      <GreenButton
        {...defaultPayButton}
        fluid
        style={{
          height: '64px',
          ...style,
        }}>
        {showTerminalSelection ? i18n('General.Cancel') : i18n('TM.Pay')}
      </GreenButton>

      {/* This is for faster flow in MultQSR */}
      {showTerminalSelection && (
        <TerminalSelectWrapper>
          <Heading level={4} bottom={16}>
            {i18n('Payment.SelectTerminal')}
          </Heading>
          <TerminalGrid>
            {storage.adyenTerminals.map((terminal) => (
              <AdyenTerminalButton
                key={terminal.POIID}
                poiid={terminal.POIID}
                handleSetupPayment={(poiid) => {
                  onPay('ADYEN', poiid);
                }}
                closeSelector={() => setShowTerminalSelection(false)}
                isbusy={terminal.POIID ? busyTerminals.includes(terminal.POIID) : false}
              />
            ))}
          </TerminalGrid>
          <BlackButton {...adyenSelectCancelButton} fluid size="large" marginTop="xs">
            {i18n('General.Cancel')}
          </BlackButton>
        </TerminalSelectWrapper>
      )}
    </PayWrapper>
  );
};

export default PayButton;
